import React, {
  useRef,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import India from "@svg-maps/india";

import "./map.scss";




// =====================================================
// REGIONS
// =====================================================

const regions = [
  {
    title: "North India",
    description:
      "Discover ancient temples and sacred destinations across North India.",
    states:
      "Uttar Pradesh • Uttarakhand • Rajasthan • Himachal Pradesh",
    image:
      "../assets/trending/kedarnath.webp",
  },

  {
    title: "South India",
    description:
      "Explore magnificent Dravidian temples and spiritual cities.",
    states:
      "Tamil Nadu • Karnataka • Kerala • Andhra Pradesh",
    image:
      "../assets/trending/rameshwaram.webp",
  },

  {
    title: "West India",
    description:
      "Journey through some of India's most iconic pilgrimage destinations.",
    states:
      "Gujarat • Maharashtra • Goa • Rajasthan",
    image:
      "../assets/trending/dwarka.webp",
  },

  {
    title: "East India",
    description:
      "Explore sacred temples, traditions and spiritual heritage.",
    states:
      "Odisha • West Bengal • Jharkhand • Bihar",
    image:
      "../assets/trending/jaganath-puri.webp",
  },

  {
    title: "Central India",
    description:
      "Discover ancient shrines and hidden spiritual destinations.",
    states:
      "Madhya Pradesh • Chhattisgarh",
    image:
      "../assets/trending/mahakaleshwar.webp",
  },

  {
    title: "Northeast India",
    description:
      "Experience unique temples and sacred traditions of Northeast India.",
    states:
      "Assam • Sikkim • Tripura • Manipur",
    image:
      "../assets/trending/kamakhya.webp",
  },
];







// =====================================================
// COMPONENT
// =====================================================

const IndiaMap = () => {
  const navigate = useNavigate();

  const mapWrapperRef =
    useRef(null);

  const [hoveredState, setHoveredState] =
    useState(null);

  const [tooltipPosition, setTooltipPosition] =
    useState({
      left: 0,
      top: 0,
    });

  const [searchValue, setSearchValue] =
    useState("");


  // ===================================================
  // STATE URL
  // ===================================================

  const getStateUrl = (
    location
  ) => {
    return `/${location.id}-templs`;
  };


  // ===================================================
  // STATE HOVER
  // ===================================================

  const handleMouseEnter = (
    event,
    location
  ) => {
    const stateElement =
      event.currentTarget;

    const wrapper =
      mapWrapperRef.current;

    if (
      !stateElement ||
      !wrapper
    ) {
      return;
    }

    const stateRect =
      stateElement.getBoundingClientRect();

    const wrapperRect =
      wrapper.getBoundingClientRect();


    // State center

    const stateCenterX =
      stateRect.left +
      stateRect.width / 2;

    const stateTop =
      stateRect.top;


    let left =
      stateCenterX -
      wrapperRect.left;

    let top =
      stateTop -
      wrapperRect.top -
      14;


    // Tooltip width

    const tooltipWidth = 210;

    const halfTooltip =
      tooltipWidth / 2;


    // Left boundary

    if (
      left < halfTooltip
    ) {
      left = halfTooltip;
    }


    // Right boundary

    if (
      left >
      wrapperRect.width -
        halfTooltip
    ) {
      left =
        wrapperRect.width -
        halfTooltip;
    }


    // Top boundary

    if (top < 60) {
      top = 60;
    }


    setHoveredState(
      location.name
    );

    setTooltipPosition({
      left,
      top,
    });
  };


  // ===================================================
  // STATE LEAVE
  // ===================================================

  const handleMouseLeave = () => {
    setHoveredState(null);
  };


  // ===================================================
  // STATE CLICK
  // ===================================================

  const handleStateClick = (
    location
  ) => {
    navigate(
      getStateUrl(location)
    );
  };


  // ===================================================
  // SEARCH
  // ===================================================

  const handleSearch = (
    event
  ) => {
    event.preventDefault();

    const value =
      searchValue
        .trim()
        .toLowerCase();

    if (!value) {
      return;
    }

    const matchedState =
      India.locations.find(
        (location) =>
          location.name
            .toLowerCase()
            .includes(value) ||
          location.id
            .toLowerCase()
            .includes(value)
      );

    if (matchedState) {
      navigate(
        getStateUrl(
          matchedState
        )
      );

      return;
    }

    navigate(
      `/search?q=${encodeURIComponent(
        searchValue
      )}`
    );
  };


  // ===================================================
  // RENDER
  // ===================================================

  return (
    <main className="indiaTemplePage">


      {/* ==================================================
          HERO
      ================================================== */}

      <section className="templeMapHero">

        <div className="heroGlow heroGlowOne"></div>

        <div className="heroGlow heroGlowTwo"></div>


        <div className="container">

          <div className="heroContent">

            <span className="heroBadge">
              <span>✦</span>

              Discover India's Temples
            </span>


            <h1>
              Explore Temples
              <span>
                Across India
              </span>
            </h1>


            <p>
              Discover sacred temples,
              spiritual destinations and
              ancient pilgrimage sites
              across every state of India.
            </p>


            {/* SEARCH */}

            <form
              className="templeSearch"
              onSubmit={
                handleSearch
              }
            >

              <span className="searchIcon">
                🔍
              </span>

              <input
                type="text"
                value={
                  searchValue
                }
                onChange={(event) =>
                  setSearchValue(
                    event.target.value
                  )
                }
                placeholder="Search a state, city or temple..."
              />

              <button
                type="submit"
              >
                Search
              </button>

            </form>


            <div className="heroHint">
              Try searching:
              <button
                type="button"
                onClick={() => {
                  setSearchValue(
                    "Uttar Pradesh"
                  );
                }}
              >
                Uttar Pradesh
              </button>

              <button
                type="button"
                onClick={() => {
                  setSearchValue(
                    "Rajasthan"
                  );
                }}
              >
                Rajasthan
              </button>

              <button
                type="button"
                onClick={() => {
                  setSearchValue(
                    "Gujarat"
                  );
                }}
              >
                Gujarat
              </button>
            </div>

          </div>

        </div>

      </section>


      {/* ==================================================
          INDIA MAP
      ================================================== */}

      <section className="indiaMapSection">

        <div className="container">


          <div className="sectionHeading">

            <span className="sectionBadge">
              Explore by State
            </span>

            <h2>
              Choose a State
            </h2>

            <p>
              Hover over a state to see
              its name and click to explore
              temples from that region.
            </p>

          </div>


          {/* MAP CARD */}

          <div className="indiaMapOuter">

            <div className="mapGlow"></div>


            <div
              className="indiaMapWrapper"
              ref={mapWrapperRef}
            >

              <svg
                className="indiaMap"
                viewBox={
                  India.viewBox
                }
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMidYMid meet"
              >

                {India.locations.map(
                  (location) => (

                    <path
                      key={
                        location.id
                      }

                      id={
                        location.id
                      }

                      d={
                        location.path
                      }

                      className="indiaMapState"


                      onMouseEnter={(
                        event
                      ) =>
                        handleMouseEnter(
                          event,
                          location
                        )
                      }


                      onMouseLeave={
                        handleMouseLeave
                      }


                      onClick={() =>
                        handleStateClick(
                          location
                        )
                      }


                      role="link"

                      tabIndex={0}

                      aria-label={
                        location.name
                      }


                      onKeyDown={(
                        event
                      ) => {

                        if (
                          event.key ===
                            "Enter" ||
                          event.key ===
                            " "
                        ) {

                          event.preventDefault();

                          handleStateClick(
                            location
                          );
                        }

                      }}

                    />

                  )
                )}

              </svg>


              {/* TOOLTIP */}

              {hoveredState && (

                <div
                  className="stateTooltip"

                  style={{
                    left: `${tooltipPosition.left}px`,
                    top: `${tooltipPosition.top}px`,
                  }}
                >

                  <div className="tooltipTop">

                    <span className="tooltipDot"></span>

                    <span className="tooltipName">
                      {
                        hoveredState
                      }
                    </span>

                  </div>


                  <div className="tooltipBottom">

                    <span>
                      Explore Temples
                    </span>

                    <span className="tooltipArrowIcon">
                      →
                    </span>

                  </div>


                  <span className="tooltipArrow"></span>

                </div>

              )}

            </div>


            {/* MAP LEGEND */}

            <div className="mapLegend">

              <span className="legendDot"></span>

              <span>
                Hover & click a state
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* ==================================================
          STATS
      ================================================== */}

      <section className="mapStatsSection">

        <div className="container">

          <div className="mapStats">

            <div className="mapStat">

              <strong>
                28
              </strong>

              <span>
                States
              </span>

            </div>


            <div className="statDivider"></div>


            <div className="mapStat">

              <strong>
                8
              </strong>

              <span>
                Union Territories
              </span>

            </div>


            <div className="statDivider"></div>


            <div className="mapStat">

              <strong>
                1000+
              </strong>

              <span>
                Temples
              </span>

            </div>


            <div className="statDivider"></div>


            <div className="mapStat">

              <strong>
                100+
              </strong>

              <span>
                Sacred Cities
              </span>

            </div>

          </div>

        </div>

      </section>


      {/* ==================================================
          REGIONS
      ================================================== */}

      <section className="regionsSection">

        <div className="container">

          <div className="sectionHeading leftHeading">

            <span className="sectionBadge">
              Explore India
            </span>

            <h2>
              Explore by Region
            </h2>

            <p>
              Discover temples and
              spiritual destinations
              region by region.
            </p>

          </div>


          <div className="regionsGrid">

            {regions.map(
              (region) => (

                <div
                  className="regionCard"
                  key={
                    region.title
                  }

                  onClick={() =>
                    navigate(
                      `/regions/${region.title
                        .toLowerCase()
                        .replace(
                          /\s+/g,
                          "-"
                        )}`
                    )
                  }
                >

                  <div className="regionImage">

                    <img
                      src={
                        region.image
                      }

                      alt={
                        region.title
                      }

                      loading="lazy"
                    />

                    <div className="imageOverlay"></div>

                  </div>


                  <div className="regionContent">

                    <h3>
                      {
                        region.title
                      }
                    </h3>

                    <p>
                      {
                        region.description
                      }
                    </p>

                    <span className="regionStates">
                      {
                        region.states
                      }
                    </span>

                    <span className="exploreLink">
                      Explore
                      <span>
                        →
                      </span>
                    </span>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </section>



     

      

      


   

      {/* ==================================================
          FINAL CTA
      ================================================== */}

      <section className="mapFinalCta">

        <div className="ctaGlow"></div>

        <div className="container">

          <div className="ctaContent">

            <span className="ctaIcon">
              ✦
            </span>

            <h2>
              Where Will Your
              <span>
                Journey Begin?
              </span>
            </h2>

            <p>
              Choose a state and discover
              the temples, stories and
              spiritual experiences waiting
              for you.
            </p>

            <button
              type="button"
              onClick={() => {
                window.scrollTo({
                  top: 0,
                  behavior: "smooth",
                });
              }}
            >
              Explore India
              <span>
                ↑
              </span>
            </button>

          </div>

        </div>

      </section>

    </main>
  );
};

export default IndiaMap;